import { Link, Outlet } from 'react-router';
import { useAuth } from './AuthContext';

/**
 * Route guard for the partner area. By the time this renders the AuthGate has
 * already ensured a signed-in user and resolved their roles, so someone without
 * a partner account gets a clear explanation rather than a redirect loop.
 *
 * Narrower than the gate above it, and still needed because of the gap: AuthGate
 * admits admins and partners alike, so what reaches here without `isPartner` is
 * an Eighty Days admin opening a partner's page. A stranger never gets this far
 * — they are stopped at the gate, by NoAccessScreen.
 *
 * A disabled account is treated as no account — `isPartner` is false — which is
 * what makes disabling a partner user in the CMS lock them out immediately.
 */
export function RequirePartner() {
  const { isPartner, partnerUser } = useAuth();

  if (!isPartner) {
    const disabled = partnerUser?.status === 'disabled';
    return (
      <div className='tw:flex tw:min-h-screen tw:flex-col tw:items-center tw:justify-center tw:bg-ed-cream tw:px-6 tw:text-center'>
        <h1 className='tw:font-serif tw:text-3xl tw:text-ed-black'>
          {disabled ? 'Account disabled' : 'No partner account'}
        </h1>
        <p className='tw:mt-3 tw:max-w-md tw:text-ed-charcoal/70'>
          {disabled
            ? 'This account has been disabled. Please contact Eighty Days if you think this is a mistake.'
            : "You're signed in, but this area is for Eighty Days partners. If you were sent an invitation link, open it to set up your account."}
        </p>
        <Link
          to='/'
          className='tw:mt-8 tw:bg-ed-gold tw:px-6 tw:py-3 tw:text-sm tw:tracking-widest tw:text-ed-black tw:uppercase tw:transition-colors tw:hover:bg-ed-black tw:hover:text-ed-cream'
        >
          Back to experiences
        </Link>
      </div>
    );
  }

  return <Outlet />;
}
